<template>
  <AppModal :aria-label="modalTitle"
            :title="modalTitle"
            :visible.sync="isVisible"
            aria-modal="true"
            class="team-training-modal"
            max-height="90vh"
            role="dialog"
            width="620px"
            @close="close">
    <div v-if="loading" class="training-modal-state" role="status">
      <span uk-spinner="ratio: 0.8"></span>
      <span>{{ $t('general.loading_training_status') }}</span>
    </div>

    <div v-else-if="errorMessage"
         class="training-modal-state training-modal-state--error"
         role="alert">
      <i aria-hidden="true" class="uil-exclamation-triangle"></i>
      <strong>{{ $t('general.training_load_failed') }}</strong>
      <span>{{ errorMessage }}</span>
      <button type="button" @click="loadStatus">{{ $t('general.try_again') }}</button>
    </div>

    <template v-else-if="status && employeeData">
      <section :aria-label="$t('general.employee_info')" class="training-user-summary">
        <UserProfileAvatar :name="employeeData.full_name || ''"
                           :photo="employeeData.avatar || ''"
                           class="training-user-avatar"
        />
        <div>
          <strong>{{ employeeData.full_name || "—" }}</strong>
          <span>{{ employeeData.email || "—" }}</span>
          <small>
            {{ displayValue(employeeData.department_name) }} ·
            {{ displayValue(employeeData.title_name) }}
          </small>
        </div>
      </section>

      <section :aria-label="$t('general.training_assignments_summary')" class="training-stat-grid">
        <article class="training-stat training-stat--total">
          <span aria-hidden="true" class="training-stat__icon">
            <i class="uil-books"></i>
          </span>
          <div>
            <strong>{{ safeCount(status.assignments?.total) }}</strong>
            <span>{{ $t('general.total_assignments') }}</span>
          </div>
        </article>
        <article class="training-stat training-stat--progress">
          <span aria-hidden="true" class="training-stat__icon">
            <i class="uil-play-circle"></i>
          </span>
          <div>
            <strong>{{ safeCount(status.assignments?.in_progress) }}</strong>
            <span>{{ $t('general.in_progress') }}</span>
          </div>
        </article>
        <article class="training-stat training-stat--completed">
          <span aria-hidden="true" class="training-stat__icon">
            <i class="uil-check-circle"></i>
          </span>
          <div>
            <strong>{{ safeCount(status.assignments?.completed) }}</strong>
            <span>{{ $t('general.completed') }}</span>
          </div>
        </article>
      </section>

      <section :aria-label="$t('general.completion_rate')" class="training-completion">
        <div class="training-completion__heading">
          <span>{{ $t('general.completion_rate') }}</span>
          <strong>%{{ completionRate }}</strong>
        </div>
        <div :aria-label="$t('general.completion_rate_aria')"
             :aria-valuenow="completionRate"
             aria-valuemax="100"
             aria-valuemin="0"
             class="training-completion__track"
             role="progressbar">
          <span :style="{ width: `${completionRate}%` }"></span>
        </div>
      </section>

      <section :aria-label="$t('general.last_login_info')" class="training-last-login">
        <span aria-hidden="true" class="training-last-login__icon">
          <i class="uil-clock"></i>
        </span>
        <div>
          <span>{{ $t('general.last_system_login') }}</span>
          <strong>{{ formatDateTime(employeeData.last_login_at) }}</strong>
        </div>
      </section>
    </template>

    <template #footer>
      <button class="training-modal-close" type="button" @click="close">
        {{ $t('general.close') }}
      </button>
    </template>
  </AppModal>
</template>

<script>
import moment from "moment";
import UserProfileAvatar from "@/view/components/common/UserProfileAvatar.vue";
import AppModal from "@/view/components/ui/AppModal.vue";
import ApiService from "@/core/services/api.service";

export default {
  name: "EducationalStatusModal",
  components: {
    AppModal,
    UserProfileAvatar,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    employee: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      employeeData: null,
      status: null,
      loading: false,
      errorMessage: "",
      requestSequence: 0,
    };
  },
  computed: {
    isVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit("update:visible", value);
      },
    },
    modalTitle() {
      const name = this.employeeData?.full_name || this.employee?.full_name;
      return name
          ? this.$t("general.training_status_with_name", { name })
          : this.$t("general.training_status");
    },
    completionRate() {
      const total = this.safeCount(this.status?.assignments?.total);
      const completed = this.safeCount(this.status?.assignments?.completed);
      if (!total) return 0;
      return Math.min(100, Math.round((completed / total) * 100));
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(isVisible) {
        if (isVisible && this.employee?.id != null) {
          this.employeeData = { ...this.employee };
          this.loadStatus();
          return;
        }
        if (!isVisible) {
          this.resetState();
        }
      },
    },
  },
  beforeDestroy() {
    this.requestSequence += 1;
  },
  methods: {
    async loadStatus() {
      const employeeId = this.employeeData?.id ?? this.employee?.id;
      if (employeeId == null) return;

      const requestId = ++this.requestSequence;
      this.loading = true;
      this.errorMessage = "";
      this.status = null;

      try {
        const response = await ApiService.get(
            `api/my-team/${employeeId}/educational_status`
        );

        if (requestId !== this.requestSequence || !this.visible) return;

        const result = response?.data || null;
        if (!result || typeof result !== "object" || !result.assignments) {
          throw new Error(this.$t("general.training_status_not_found"));
        }

        if (result.user && typeof result.user === "object") {
          this.employeeData = { ...(this.employeeData || {}), ...result.user };
        }

        this.status = result;
      } catch (error) {
        if (requestId !== this.requestSequence) return;
        const status = error?.response?.status;
        this.errorMessage =
            status === 404
                ? this.$t("general.employee_not_in_team")
                : error?.response?.data?.message ||
                error?.message ||
                this.$t("general.try_later");
      } finally {
        if (requestId === this.requestSequence) {
          this.loading = false;
        }
      }
    },
    close() {
      this.requestSequence += 1;
      this.isVisible = false;
      this.$emit("close");
    },
    resetState() {
      this.requestSequence += 1;
      this.employeeData = null;
      this.status = null;
      this.loading = false;
      this.errorMessage = "";
    },
    safeCount(value) {
      const parsedValue = Number(value);
      return Number.isFinite(parsedValue) && parsedValue > 0
          ? Math.floor(parsedValue)
          : 0;
    },
    formatDateTime(value) {
      if (!value) return "—";
      const date = moment(value);
      return date.isValid() ? date.format("DD.MM.YYYY HH:mm") : "—";
    },
    displayValue(value) {
      return value === undefined || value === null || String(value).trim() === ""
          ? "—"
          : String(value);
    },
  },
};
</script>

<style scoped>
.team-training-modal :deep(.app-modal-header) {
  min-width: 0;
  gap: 12px;
}

.team-training-modal :deep(.app-modal-title) {
  min-width: 0;
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.3;
}

.team-training-modal :deep(.app-modal-close) {
  width: 44px;
  height: 44px;
  min-width: 44px;
  min-height: 44px;
  flex: 0 0 44px;
  flex-shrink: 0;
}

.training-modal-state {
  display: flex;
  min-height: 220px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 9px;
  color: #64748b;
  text-align: center;
}

.training-modal-state > i {
  color: #dc2626;
  font-size: 34px;
}

.training-modal-state strong {
  color: #334155;
}

.training-modal-state button {
  padding: 8px 12px;
  color: #fff;
  background: var(--backgroundColor, #666cff);
  border: 0;
  border-radius: 7px;
  cursor: pointer;
}

.training-user-summary {
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 15px;
  margin-bottom: 16px;
  background: #f8fafc;
  border: 1px solid #e6ebf2;
  border-radius: 11px;
}

.training-user-avatar {
  width: 50px;
  height: 50px;
  min-width: 50px;
  flex-shrink: 0;
  font-size: 16px;
}

.training-user-summary > div {
  min-width: 0;
}

.training-user-summary strong,
.training-user-summary span,
.training-user-summary small {
  display: block;
  overflow-wrap: anywhere;
}

.training-user-summary strong {
  color: #1e293b;
  font-size: 15px;
}

.training-user-summary span {
  margin-top: 2px;
  color: #64748b;
  font-size: 12px;
}

.training-user-summary small {
  margin-top: 4px;
  color: #94a3b8;
  font-size: 11px;
}

.training-stat-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.training-stat {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 10px;
  padding: 14px;
  background: #fff;
  border: 1px solid #e5eaf1;
  border-radius: 10px;
}

.training-stat__icon {
  display: inline-flex;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  align-items: center;
  justify-content: center;
  color: #3568d4;
  background: #eaf1ff;
  border-radius: 9px;
  font-size: 18px;
}

.training-stat > div {
  min-width: 0;
}

.training-stat strong,
.training-stat > div > span {
  display: block;
}

.training-stat strong {
  color: #1e293b;
  font-size: 21px;
  line-height: 1.1;
}

.training-stat > div > span {
  margin-top: 3px;
  color: #64748b;
  font-size: 10px;
  white-space: nowrap;
}

.training-stat--progress .training-stat__icon {
  color: #a16207;
  background: #fef9c3;
}

.training-stat--completed .training-stat__icon {
  color: #15803d;
  background: #dcfce7;
}

.training-completion {
  padding: 15px;
  margin-top: 14px;
  background: #f8fafc;
  border-radius: 10px;
}

.training-completion__heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 9px;
  color: #64748b;
  font-size: 12px;
}

.training-completion__heading strong {
  color: #334155;
}

.training-completion__track {
  height: 8px;
  overflow: hidden;
  background: #e2e8f0;
  border-radius: 999px;
}

.training-completion__track span {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, #3568d4, #22a06b);
  border-radius: inherit;
}

.training-last-login {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 14px 15px;
  margin-top: 14px;
  border: 1px solid #e5eaf1;
  border-radius: 10px;
}

.training-last-login__icon {
  display: inline-flex;
  width: 38px;
  height: 38px;
  align-items: center;
  justify-content: center;
  color: #7c3aed;
  background: #f3e8ff;
  border-radius: 50%;
  font-size: 19px;
}

.training-last-login span,
.training-last-login strong {
  display: block;
}

.training-last-login span {
  color: #94a3b8;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
}

.training-last-login strong {
  margin-top: 2px;
  color: #334155;
  font-size: 13px;
}

.training-modal-close {
  min-height: 38px;
  padding: 0 17px;
  color: #475569;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  cursor: pointer;
}

@media (max-width: 700px) {
  .team-training-modal :deep(.app-modal-header) {
    padding: 12px 16px;
  }

  .team-training-modal :deep(.app-modal-title) {
    font-size: 18px;
  }

  .training-user-summary {
    padding: 12px;
    margin-bottom: 12px;
  }

  .training-stat-grid {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .training-stat {
    min-height: 64px;
    padding: 12px;
  }

  .training-stat__icon {
    width: 40px;
    height: 40px;
    flex-basis: 40px;
  }

  .training-completion,
  .training-last-login {
    margin-top: 12px;
  }

  .training-modal-close {
    min-height: 48px;
    width: 100%;
  }

  .training-stat > div > span {
    white-space: normal;
  }
}
</style>
